import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageHero from '../components/PageHero';
import { Link } from 'react-router-dom';
import { useMobileMenu } from '../components/Footer';

const FaqPage: React.FC = () => {
  const { language } = useLanguage();
  const { closeMobileMenu } = useMobileMenu();

  const faqs = language === 'en' 
    ? [
        {
          question: "What makes YouCRM different from other CRM solutions?",
          answer: "YouCRM is fully customized to your specific business needs. Unlike most off-the-shelf CRM products, we don't expect you to adapt to our software - we adapt our software to your processes. This ensures maximum efficiency and user adoption."
        },
        {
          question: "How long does implementation typically take?",
          answer: "Implementation time varies depending on your specific requirements and the complexity of your processes. A basic setup can be completed in as little as 2-4 weeks, while more complex solutions might take 2-3 months. We'll provide you with a clear timeline during our initial consultation."
        },
        {
          question: "Do you offer training for our team?",
          answer: "Yes, comprehensive training is included in all our packages. We believe that proper training is essential for successful adoption of any CRM system. We provide both in-person and online training sessions, as well as documentation and tutorial videos."
        },
        {
          question: "Can YouCRM integrate with our existing software?",
          answer: "Yes, YouCRM is designed to integrate with most common business software. We have pre-built integrations for many popular accounting, marketing, and ERP systems. For specialized software, we can develop custom integrations as part of your implementation."
        },
        {
          question: "Is data migration from our current system possible?",
          answer: "Absolutely. We offer comprehensive data migration services and have experience migrating data from many different CRM and database systems. Our team will ensure that your historical data is properly transferred and validated in the new system."
        }
      ]
    : [
        {
          question: "Miben különbözik a YouCRM más CRM megoldásoktól?",
          answer: "A YouCRM teljes mértékben az Ön specifikus üzleti igényeihez igazodik. A legtöbb dobozos CRM termékkel ellentétben nem várjuk el, hogy Ön alkalmazkodjon a szoftverünkhöz - mi igazítjuk a szoftvert az Ön folyamataihoz. Ez maximális hatékonyságot és felhasználói elfogadást biztosít."
        },
        {
          question: "Általában mennyi időt vesz igénybe a bevezetés?",
          answer: "A bevezetés ideje az Ön konkrét követelményeitől és folyamatainak összetettségétől függően változik. Az alapvető beállítás akár 2-4 hét alatt is elvégezhető, míg a komplexebb megoldások 2-3 hónapot is igénybe vehetnek. Az első konzultáció során egyértelmű ütemtervet biztosítunk Önnek."
        },
        {
          question: "Biztosítanak képzést a csapatunk számára?",
          answer: "Igen, minden csomagunk tartalmaz átfogó képzést. Úgy gondoljuk, hogy a megfelelő képzés elengedhetetlen bármely CRM rendszer sikeres bevezetéséhez. Személyes és online képzési alkalmakat is biztosítunk, valamint dokumentációt és oktatóvideókat is."
        },
        {
          question: "Tud a YouCRM integrálódni a meglévő szoftvereinkkel?",
          answer: "Igen, a YouCRM úgy lett kialakítva, hogy a legtöbb általános üzleti szoftverrel integrálható legyen. Számos népszerű könyvelési, marketing és vállalatirányítási rendszerhez előre elkészített integrációkkal rendelkezünk. Speciális szoftverek esetén egyedi integrációt fejlesztünk a bevezetés részeként."
        },
        {
          question: "Lehetséges az adatok migrálása a jelenlegi rendszerünkből?",
          answer: "Természetesen. Átfogó adatmigrációs szolgáltatásokat kínálunk, és tapasztalatunk van számos különböző CRM és adatbázis-rendszerből történő adatmigrációban. Csapatunk biztosítja, hogy a historikus adatok megfelelően kerüljenek át és validálva legyenek az új rendszerben."
        }
      ];

  return (
    <div className="bg-white">
      <PageHero 
        title={language === 'en' ? 'Frequently Asked Questions' : 'Gyakran Ismételt Kérdések'} 
        subtitle={language === 'en' 
          ? 'Find answers to the most common questions about YouCRM'
          : 'Találja meg a válaszokat a YouCRM-mel kapcsolatos leggyakoribb kérdésekre'
        } 
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <dl className="space-y-8 divide-y divide-gray-200">
            {faqs.map((faq, index) => (
              <div key={index} className={`${index > 0 ? 'pt-8' : ''}`}>
                <dt className="text-xl font-semibold text-gray-900">
                  {faq.question}
                </dt>
                <dd className="mt-4 text-base text-gray-500">{faq.answer}</dd>
              </div>
            ))}
          </dl>
        </div>
        
        <div className="mt-16 text-center">
          <div className="inline-flex items-center px-6 py-4 rounded-full bg-indigo-50 text-indigo-800 mb-8">
            <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span className="font-medium">
              {language === 'en' 
                ? 'Still have questions?' 
                : 'További kérdései vannak?'}
            </span>
          </div>
          <p className="text-lg text-gray-600 mb-6">
            {language === 'en' 
              ? 'Our team is ready to assist you with any questions or concerns you may have.' 
              : 'Csapatunk készen áll, hogy segítsen Önnek bármilyen kérdésben vagy aggályban.'}
          </p>
          <Link 
            to="/contact" 
            onClick={closeMobileMenu}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:-translate-y-1"
          >
            {language === 'en' ? 'Contact Us' : 'Kapcsolat'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FaqPage; 